import '@stencil/router';
import { initSentry, setOfflineTag } from '../utils/sentry';
import { handleMobileVh } from './handleMobileVh';
import { createCustomEvent } from '../utils/createCustomEvent';
import { bootstrap } from '../store/bootstrap';
import { getSettingFromAppRoot } from '../utils/getSettingFromAppRoot';
import { store } from '../utils/store';
import { isHeaderEnabled } from '../store/isHeaderEnabled';
import { createClient } from '../createClient';
import { ConfigQuery, LocalesEnum } from '../generated/operations';
import { fetchConfigOrFail } from '../utils/fetchConfigOrFail';
import { parseSettingsFromUrlOnBelimoHost } from '../utils/parseSettingsFromUrlOnBelimoHost';
import { UserSettings } from '../types/UserSettings';
import { BelimoError } from '../components/app-error-messages/type';
import { createOfflineStorage } from 'src/offline-storage';
import { ENABLE_OFFLINE, USER_SETTINGS } from 'src/components/constants';
import { areWeInsideMobileWrapper } from 'src/utils/areWeInsideMobileWrapper';
import { loadUserSettingsFromLS } from 'src/global/loadUserSettingsFromLS';
import { defaultUserSettings } from 'src/global/defaultUserSettings';
import { createOfflineDatabase } from 'src/database/createOfflineDatabase';
import { getCountryData } from 'src/utils/parseSettingsFromUrl';

const { state } = store;

export default async () => {

  const { serverUri, apiUri, imagesProxyUri, sentryHost } = getSettingFromAppRoot(window);
  initSentry(sentryHost, window.location.hostname);
  state.imagesProxyUri = imagesProxyUri;
  state.cdnUri = serverUri;
  state.apiUri = apiUri;
  // @TODO investigate how to override css inside a shadow dom and remove this css vars
  [{ name: '--flags', path: '/img/flags.png' }, { name: '--flags2x', path: '/img/flags@2x.png' }]
  .forEach((item) => {
    window.document.documentElement.style.setProperty(
      item.name,
      `url(${serverUri}${item.path})`
    );
  });
  handleMobileVh(window);
  // hook for mobile wrappers
  window.processOcrImage = (detail: string): void => {
    window.document.dispatchEvent(createCustomEvent('processOcrImage', detail));
  }
  window.processFile = (detail: string): void => {
    window.document.dispatchEvent(createCustomEvent('processFile', detail));
  }

  const client = createClient(`${state.apiUri}graphql`, store.state.apolloCache);
  state.apollo = client;

  let config: ConfigQuery | null = null;

  try {
    store.onChange('isRegionDetected', async (isRegionDetected) => {
      const splashScreen = document.querySelector<HTMLDivElement>('.splash-screen');
      splashScreen.classList.remove('hidden');
      if (isRegionDetected) {
        splashScreen.classList.add('hidden');
      }
    });

    if (isHeaderEnabled(window)) {

      const offlineStorage = createOfflineStorage(window.localStorage);
      state.offlineStorage = offlineStorage;
      const userSettingsCandidate = await loadUserSettingsFromLS(offlineStorage);

      // are we in offline mode?
      const isOffline = (await offlineStorage.get(ENABLE_OFFLINE)) === 'on';
      state.enableOffline = isOffline || areWeInsideMobileWrapper(window);
      setOfflineTag(state.enableOffline ? 'yes' : 'no');

      if (state.enableOffline) {
        const databases = createOfflineDatabase();
        const initOffline = await import('./initOffline');
        const { config: configSrc, settings } = await initOffline.initOffline(
          userSettingsCandidate,
          offlineStorage,
          client,
          apiUri,
          databases
        );
        config = configSrc;
        state.currentCountry = settings.currentCountry;
        state.currentRegion = settings.currentRegion;
        state.currentLocale = settings.currentLocale;
        state.shoppingCenter = settings.shoppingCenter ?? '';
        state.currentLocaleAndRegion = {
          locale: state.currentLocale,
          region: state.currentRegion,
        }

        store.onChange('currentLocaleAndRegion', async () => {
            if (state.isRegionDetected === true) {
              return;
            }
            await initOffline.handleLocaleChange(
              state,
              offlineStorage,
              client,
              apiUri,
              databases,
              window,
            )
        });

      } else {
        config = await fetchConfigOrFail(
          client,
          userSettingsCandidate ? userSettingsCandidate.currentLocale : LocalesEnum.EnGb
        );
        const settings: UserSettings = userSettingsCandidate
          ? userSettingsCandidate
          : defaultUserSettings;
        await offlineStorage.set(USER_SETTINGS, JSON.stringify(settings));

        state.currentCountry = settings.currentCountry;
        state.currentRegion = settings.currentRegion;
        state.currentLocale = settings.currentLocale;
        const { data: countryData } = getCountryData(settings.currentCountry, config)
        state.shoppingCenter = countryData.shoppingCenter ?? '';
        state.currentLocaleAndRegion = {
          locale: state.currentLocale,
          region: state.currentRegion,
        }

        store.onChange('currentLocaleAndRegion', (async () => {
          if (state.isRegionDetected === true) {
            return;
          }
          const userSettingsCandidate = await loadUserSettingsFromLS(offlineStorage);
          state.config = await fetchConfigOrFail(
            client,
            userSettingsCandidate ? userSettingsCandidate.currentLocale : LocalesEnum.EnGb
          );
          state.isRegionDetected = true;
        }));
      }

    } else {


      config = await fetchConfigOrFail(client, state.currentLocale);
      const settings = parseSettingsFromUrlOnBelimoHost(config);
      if (settings != null) {
        if (settings.currentLocale != state.currentLocale) {
          config = await fetchConfigOrFail(client, settings.currentLocale);
        }
        state.currentCountry = settings.currentCountry;
        state.currentRegion = settings.currentRegion;
        state.currentLocale = settings.currentLocale;
        const { data: countryData } = getCountryData(settings.currentCountry, config)
        state.shoppingCenter = countryData.shoppingCenter ?? '';
        state.currentLocaleAndRegion = {
          locale: state.currentLocale,
          region: state.currentRegion,
        }
      }

    }

    state.config = config;

    state.isRegionDetected = true;

    bootstrap().then(() => null);

  } catch (e) {
    console.error(e);
    if (state.config === null) {
      state.config = {
        i18n: {
          ERROR_TITLE: 'Fatal error',
          UNRECOVERABLE_ERROR_MESSAGE: 'Check internet connection and try to reload or reinstall app',
        }
      } as any;
    }
    setTimeout(() => {
      const detail: BelimoError = { type: state.enableOffline ? 'noConnection' : 'unknown', cause: e };
      document.dispatchEvent(new CustomEvent('belimoError', { detail, bubbles: true }));
    }, 50);
  }
};
